import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import {
  Box, Drawer, List, Typography, useMediaQuery, Divider
} from '@mui/material';
import useAuth from 'src/context/AuthContext';
import { getAppRoutes } from 'src/utils/routeConstants';
import { USER_ROLES } from 'src/utils/constants';
import { useDispatch, useSelector } from 'react-redux';
import { getApprovalsCount } from 'src/redux/cardApprovalSlice';
import { getUnreadNotificationsCount } from 'src/redux/notificationSlice';
import { isAdminUser } from 'src/utils/getInitials';
import getStaticAssetUrl from 'src/utils/getStaticAssetUrl';
import { FLEET_ONLY_BRAND, isFleetOnlyModeEnabled } from 'src/utils/appMode';
import NavItem from './NavItem';
import { SIDE_NAV_WIDTH } from './DashboardNavbar';

const DashboardSidebar = ({
  onMobileClose,
  openMobile,
  isCollapsed = false,
  mobileCollapsed = false,
  width = SIDE_NAV_WIDTH,
  onToggleCollapse,
  onToggleMobileCollapse,
  onDesktopMouseEnter,
  onDesktopMouseLeave,
  onNavigate
}) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const { getUser } = useAuth();
  const user = getUser();
  const { appSettings } = useSelector((state) => state.settings);
  const isFleetOnlyMode = isFleetOnlyModeEnabled(appSettings);
  const isAdmin = isAdminUser(getUser);
  const isApprover = user?.role === USER_ROLES.approver;
  const isOperator = user?.role === USER_ROLES.operator;
  const isSupport = user?.role === USER_ROLES.support;
  const lgUp = useMediaQuery((th) => th.breakpoints.up('lg'));

  const appRoutes = getAppRoutes(user, appSettings);
  const mobileDrawerWidth = mobileCollapsed ? SIDE_NAV_WIDTH * (80 / 280) : SIDE_NAV_WIDTH;

  useEffect(() => {
    if (isOperator || isSupport) {
      return;
    }
    if ((isAdmin || isApprover) && !!location.state?.fromDashboard) {
      dispatch(getApprovalsCount());
    }
    dispatch(getUnreadNotificationsCount());
  }, [dispatch, isAdmin, isApprover, isOperator, isSupport, location.pathname, location.state]);

  const navCollapsed = lgUp ? isCollapsed : mobileCollapsed;
  const drawerWidth = lgUp ? width : mobileDrawerWidth;

  const content = (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: isFleetOnlyMode ? FLEET_ONLY_BRAND.ink : '#1F2937',
        color: '#ffffff',
        overflow: 'hidden',
        borderRight: isFleetOnlyMode
          ? `1px solid ${FLEET_ONLY_BRAND.border}`
          : '1px solid rgba(255,255,255,0.05)'
      }}
    >
      {/* Brand Section */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: navCollapsed ? 'center' : 'flex-start',
          px: navCollapsed ? 1 : 3,
          minHeight: 80,
          transition: 'all 0.3s ease',
          background: isFleetOnlyMode
            ? `linear-gradient(180deg, ${FLEET_ONLY_BRAND.primary} 0%, ${FLEET_ONLY_BRAND.ink} 100%)`
            : 'linear-gradient(180deg, rgba(255,255,255,0.03) 0%, rgba(0,0,0,0) 100%)'
        }}
      >
        <Box
          component="img"
          src={getStaticAssetUrl('/static/images/logo.png')}
          alt="Logo"
          sx={{
            height: navCollapsed ? 32 : 36,
            width: 'auto',
            transition: 'all 0.3s ease',
            filter: isFleetOnlyMode
              ? `drop-shadow(0 0 18px ${FLEET_ONLY_BRAND.glow})`
              : 'drop-shadow(0 0 8px rgba(16, 185, 129, 0.4))'
          }}
        />
        {!navCollapsed && (
          <Box sx={{ ml: 2, transition: 'opacity 0.3s ease' }}>
            <Typography variant="h6" sx={{ fontWeight: 800, letterSpacing: 0.5, lineHeight: 1 }}>
              RETRACK
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: isFleetOnlyMode ? '#B8D6E7' : '#10B981',
                fontWeight: 700,
                letterSpacing: 2
              }}
            >
              {isFleetOnlyMode ? 'FLEET COMMAND' : 'AUTOMATION'}
            </Typography>
          </Box>
        )}
      </Box>

      <Divider
        sx={{
          borderColor: isFleetOnlyMode ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.08)',
          mx: 2,
          mb: 2
        }}
      />

      {/* Navigation */}
      <Box
        sx={{
          flexGrow: 1,
          px: 2,
          pb: 2,
          overflowY: 'auto',
          '&::-webkit-scrollbar': { width: '4px' },
          '&::-webkit-scrollbar-track': { background: 'transparent' },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255,255,255,0.2)',
            borderRadius: 4
          },
          '&::-webkit-scrollbar-thumb:hover': { background: 'rgba(255,255,255,0.3)' }
        }}
      >
        <List disablePadding>
          {appRoutes.map((item) => (
            <NavItem
              key={item.title}
              item={item}
              collapsed={navCollapsed}
              onToggleCollapse={lgUp ? onToggleCollapse : onToggleMobileCollapse}
              onNavigate={onNavigate}
            />
          ))}
        </List>
      </Box>

      {/* Footer / Version removed */}
    </Box>
  );

  const sidebarStyle = {
    border: 'none',
    width: drawerWidth,
    bgcolor: isFleetOnlyMode ? FLEET_ONLY_BRAND.ink : '#1F2937',
    color: '#fff',
    transition: 'width 300ms cubic-bezier(0.4, 0, 0.2, 1)',
    overflowX: 'hidden',
    boxShadow: isFleetOnlyMode
      ? '10px 0 30px 0 rgba(16,32,54,0.14)'
      : '4px 0 24px 0 rgba(0,0,0,0.1)',
  };

  if (lgUp) {
    return (
      <Drawer
        anchor="left"
        open
        variant="permanent"
        PaperProps={{ sx: sidebarStyle }}
        onClick={navCollapsed ? onDesktopMouseEnter : undefined}
        onMouseEnter={onDesktopMouseEnter}
        onMouseLeave={onDesktopMouseLeave}
      >
        {content}
      </Drawer>
    );
  }

  return (
    <Drawer
      anchor="left"
      onClose={onMobileClose}
      open={openMobile}
      variant="temporary"
      PaperProps={{ sx: sidebarStyle }}
    >
      {content}
    </Drawer>
  );
};

DashboardSidebar.propTypes = {
  onMobileClose: PropTypes.func,
  openMobile: PropTypes.bool,
  isCollapsed: PropTypes.bool,
  mobileCollapsed: PropTypes.bool,
  width: PropTypes.number,
  onToggleCollapse: PropTypes.func,
  onToggleMobileCollapse: PropTypes.func,
  onDesktopMouseEnter: PropTypes.func,
  onDesktopMouseLeave: PropTypes.func,
  onNavigate: PropTypes.func
};

DashboardSidebar.defaultProps = {
  onMobileClose: () => { },
  openMobile: false,
  mobileCollapsed: false,
  onToggleCollapse: () => { },
  onToggleMobileCollapse: () => { },
  onDesktopMouseEnter: () => { },
  onDesktopMouseLeave: () => { },
  onNavigate: () => { }
};

export default DashboardSidebar;
